import React from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import CrossIcon from '@atlaskit/icon/core/cross';

import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';

import AtomIcon from '@atlaskit/icon-lab/core/atom';

import StatusWorkflowScheduledIcon from '@atlaskit/icon-lab/core/status-workflow-scheduled';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { token } from '@atlaskit/tokens';

import messages from './messages';

import { type JSX } from 'react/jsx-runtime';

const gridBackgroundImage = `radial-gradient(circle, ${token(
	'color.border.disabled',
)} 0, ${token('color.border.disabled')} 1px, transparent 1.5px), radial-gradient(circle, ${token(
	'color.border.disabled',
)} 0, ${token('color.border.disabled')} 1px, transparent 1.5px)`;

const primaryTaskMetaStatusStyle: React.CSSProperties = {
	background: `linear-gradient(90deg, ${token('color.text.subtlest')} 0%, ${token(
		'color.text.disabled',
	)} 100%)`,
	backgroundClip: 'text',
	display: 'inline-block',
	maxWidth: '126px',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	verticalAlign: 'bottom',
	whiteSpace: 'nowrap',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
};

const styles = {
	bannerContainer: xcss({
		aspectRatio: '700 / 162',
		backgroundColor: 'elevation.surface.sunken',
		backgroundImage: gridBackgroundImage,
		backgroundPosition: '32px 0, 0 32px',
		backgroundRepeat: 'repeat, repeat',
		backgroundSize: '32px 8px, 8px 32px',
		borderRadius: 'radius.xlarge',
		boxSizing: 'border-box',
		marginInline: 'auto',
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
	}),
	content: xcss({
		maxWidth: '349px',
		paddingBlockStart: 'space.150',
		position: 'relative',
		zIndex: 'layer',
	}),
	dismissButton: xcss({
		position: 'absolute',
		right: 'space.200',
		top: 'space.050',
		zIndex: 'spotlight',
	}),
	maxMark: xcss({
		color: 'color.text.subtle',
	}),
	artwork: xcss({
		bottom: 'space.0',
		left: 'space.0',
		pointerEvents: 'none',
		position: 'absolute',
		top: 'space.0',
		width: '100%',
		zIndex: '1',
	}),
	ribbon: xcss({
		height: '233px',
		left: '36.57%',
		position: 'absolute',
		top: '-28px',
		width: '58.25%',
	}),
	taskCard: xcss({
		alignItems: 'center',
		backgroundColor: 'elevation.surface.overlay',
		borderColor: 'color.border',
		borderRadius: 'radius.xlarge',
		borderStyle: 'solid',
		borderWidth: 'border.width',
		boxShadow: 'elevation.shadow.overlay',
		boxSizing: 'border-box',
		display: 'flex',
		height: '51px',
		overflow: 'hidden',
		paddingBlock: 'space.100',
		paddingInline: 'space.150',
		position: 'absolute',
	}),
	primaryTaskCard: xcss({
		left: '53.43%',
		top: '31px',
		width: '87%',
	}),
	secondaryTaskCard: xcss({
		left: '64.84%',
		top: '88px',
		width: '46.88%',
	}),
	taskIcon: xcss({
		alignItems: 'center',
		borderRadius: 'radius.large',
		color: 'color.text.inverse',
		display: 'flex',
		flexShrink: 0,
		height: '32px',
		justifyContent: 'center',
		width: '32px',
	}),
	primaryTaskIcon: xcss({
		backgroundColor: 'color.background.accent.blue.bolder',
	}),
	secondaryTaskIcon: xcss({
		backgroundColor: 'color.background.accent.purple.bolder',
	}),
	taskContent: xcss({
		minWidth: '0px',
		width: '100%',
	}),
	taskText: xcss({
		minWidth: '0px',
		overflow: 'hidden',
	}),
} as const;

const RibbonArtwork = (): JSX.Element => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 407.732 233.259"
		width="100%"
		height="100%"
		fill="none"
		aria-hidden="true"
		focusable="false"
	>
		<g transform="translate(-2.218 79.223) rotate(-23.55 206.084 37.407) translate(0 -6.92)">
			<path
				d="M0.333038 56.0945C7.17988 56.4244 31.6222 62.0943 45.5785 56.0945C60.3219 49.7563 67.3744 34.9132 83.6207 36.3083C96.6766 37.4295 109.205 50.9836 110.183 67.4758"
				stroke={token('color.rovo.icon.saffron')}
				strokeWidth="13.8423"
			/>
			<path
				d="M110.133 67.4748C110.827 79.1222 95.3246 79.1346 90.5493 67.4748C90.0707 66.3062 89.4567 64.611 89.1706 63.2169C84.2464 39.2224 95.5809 25.1317 106.349 25.1312M232.02 50.9963C253.552 55.1906 289.57 54.8411 338.899 41.2095C390.597 26.9229 405.468 26.4617 412.5 28.6266"
				stroke={token('color.chart.categorical.2')}
				strokeWidth="13.8423"
			/>
			<path
				d="M106.092 25.1321C125.673 25.1311 122.936 79.8857 150.357 81.6858C164.685 82.6264 175.745 69.6606 182.39 55.3668M176.211 7.30626C166.81 9.61309 169.16 19.8892 176.211 26.8798C182.307 32.9239 212.122 47.1594 231.836 50.9972C253.381 55.1916 289.42 54.842 338.777 41.2104C390.507 26.9238 405.386 26.4626 412.422 28.6275"
				stroke={token('color.chart.categorical.3')}
				strokeWidth="13.8423"
			/>
			<path
				d="M412.422 28.628C405.386 26.4631 390.507 26.9243 338.778 41.2109C289.421 54.8425 253.382 55.192 231.837 50.9977C212.123 47.1598 182.308 32.9244 176.212 26.8803C169.161 19.8897 166.811 9.61367 176.212 7.30685C188.747 4.23108 190.314 20.2394 189.531 28.628C189.299 34.809 186.217 47.0709 182.391 55.3673"
				stroke={token('color.background.accent.blue.bolder')}
				strokeWidth="13.8423"
			/>
		</g>
	</svg>
);

type TaskCardProps = {
	title: string;
	meta: string;
	metaTimestamp?: string;
	variant: 'primary' | 'secondary';
};

const TaskCard = ({ title, meta, metaTimestamp, variant }: TaskCardProps): JSX.Element => (
	<Box
		xcss={[
			styles.taskCard,
			variant === 'primary' ? styles.primaryTaskCard : styles.secondaryTaskCard,
		]}
	>
		<Inline xcss={styles.taskContent} space="space.100" alignBlock="center">
			<Box
				xcss={[
					styles.taskIcon,
					variant === 'primary' ? styles.primaryTaskIcon : styles.secondaryTaskIcon,
				]}
			>
				<StatusWorkflowScheduledIcon label="" color="currentColor" />
			</Box>
			<Stack xcss={styles.taskText}>
				<Text size="medium" weight="semibold" maxLines={1}>
					{title}
				</Text>
				<Text size="small" color="color.text.subtlest" maxLines={1}>
					{metaTimestamp ? (
						<>
							<span style={primaryTaskMetaStatusStyle}>{meta}</span>
							<span>{` • ${metaTimestamp}`}</span>
						</>
					) : (
						meta
					)}
				</Text>
			</Stack>
		</Inline>
	</Box>
);

type MaxFullPageBannerProps = {
	onDismiss: () => void;
	onTryMax: () => void;
};

export const MaxFullPageBanner = ({ onDismiss, onTryMax }: MaxFullPageBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.bannerContainer}
			paddingBlockStart="space.050"
			paddingBlockEnd="space.200"
			paddingInline="space.200"
			testId="rovo-chat-long-running-task-fullpage-banner"
		>
			<Box xcss={styles.dismissButton}>
				<IconButton
					appearance="subtle"
					icon={(iconProps) => (
						<CrossIcon {...iconProps} color={token('color.icon.subtlest')} size="small" />
					)}
					label={formatMessage(messages.dismissButton)}
					onClick={onDismiss}
					spacing="default"
				/>
			</Box>
			<Box
				xcss={styles.artwork}
				aria-hidden="true"
				testId="rovo-chat-long-running-task-fullpage-banner-artwork"
			>
				<Box xcss={styles.ribbon}>
					<RibbonArtwork />
				</Box>
				<TaskCard
					variant="primary"
					title={formatMessage(messages.primaryTaskTitle)}
					meta={formatMessage(messages.primaryTaskMeta)}
					metaTimestamp={formatMessage(messages.primaryTaskMetaTimestamp)}
				/>
				<TaskCard
					variant="secondary"
					title={formatMessage(messages.secondaryTaskTitle)}
					meta={formatMessage(messages.secondaryTaskMeta)}
				/>
			</Box>
			<Stack xcss={styles.content} space="space.150">
				<Inline xcss={styles.maxMark} space="space.050" alignBlock="center">
					<AtomIcon label="" color={token('color.icon.subtle')} />
					<Text color="color.text.subtle">{formatMessage(messages.maxLabel)}</Text>
				</Inline>
				<Stack space="space.050">
					<Heading size="xsmall">{formatMessage(messages.headline)}</Heading>
					<Text size="small" color="color.text.subtle">
						{formatMessage(messages.body)}
					</Text>
				</Stack>
				<Inline>
					<Button appearance="primary" iconBefore={RovoChatIcon} onClick={onTryMax}>
						{formatMessage(messages.ctaButton)}
					</Button>
				</Inline>
			</Stack>
		</Box>
	);
};
